import { Button, Flex, Form, type FormInstance, type FormListFieldData, Input, Tooltip, Typography } from "antd";
import { FormItemGroup } from "@vyne-shared/components/vyneForm/items/VyneFormItems";
import type { TerminalFormType, TerminalSetting } from "./useTerminalForm";

const { Title } = Typography;

interface TerminalSettingInputProps {
    readonly field: FormListFieldData;
    readonly form: FormInstance<TerminalFormType>;
    readonly index: number;
    readonly onRemove: (index: number | number[]) => void;
}

const labelChangeDisabled = (terminal: TerminalSetting) => terminal.action?.status === "in_progress";
const deletionDisabled = (terminal: TerminalSetting) => labelChangeDisabled(terminal) || terminal.status === "online";

const isSimulatedTerminal = (terminal: TerminalSetting) =>
    terminal.deviceType === "simulated_stripe_s700" || terminal.deviceType === "simulated_wisepos_e";

export const TerminalSettingInput = ({ field, form, index, onRemove }: TerminalSettingInputProps) => {
    const terminal = Form.useWatch(["terminals", index], form) as TerminalSetting | undefined;

    return (
        terminal && (
            <FormItemGroup
                legendTitle={
                    terminal.serialNumber
                        ? `Terminal SN# ${terminal.serialNumber}${isSimulatedTerminal(terminal) ? " (Simulated)" : ""}`
                        : "New Terminal"
                }
                innerCard={false}
            >
                <Flex gap="middle" align="flex-end" justify="center" className="terminal-settings-form-item-control">
                    <TerminalNameInput field={field} disabled={labelChangeDisabled(terminal)} />
                    {!terminal.id && <TerminalRegistrationCodeInput field={field} />}
                    <Tooltip
                        title={deletionDisabled(terminal) ? "Device must be powered off before deleting" : ""}
                        placement="topRight"
                    >
                        <Button
                            type="link"
                            danger={true}
                            className="terminal-settings-form-item-control-delete-button"
                            onClick={() => onRemove(index)}
                            disabled={deletionDisabled(terminal)}
                        >
                            Delete
                        </Button>
                    </Tooltip>
                </Flex>
                {labelChangeDisabled(terminal) && <Title level={2}>Terminal is being used</Title>}
            </FormItemGroup>
        )
    );
};

interface TerminalNameInputProps {
    readonly field: FormListFieldData;
    readonly disabled?: boolean;
}

const TerminalNameInput = ({ field, disabled }: TerminalNameInputProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- need to discard key
    const { name, key, ...fieldProps } = field;

    return (
        <Form.Item
            className="terminal-settings-form-item-control-label"
            label="Terminal Name"
            dependencies={[name, "registrationCode"]}
            name={[name, "label"]}
            rules={[
                {
                    message: "Invalid terminal name (A-Z, 0-9, Spaces, Underscores, Dashes and Dots only)",
                    pattern: /^[\w\s.-]{1,32}$/, // A-Z, 0-9, Spaces, Underscores, Dashes and Dots only
                    whitespace: true,
                    required: true,
                },
            ]}
            validateTrigger={["onChange", "onBlur"]}
            {...fieldProps}
        >
            <Input disabled={disabled} minLength={1} maxLength={32} />
        </Form.Item>
    );
};

interface TerminalRegistrationCodeInputProps {
    readonly field: FormListFieldData;
}

const TerminalRegistrationCodeInput = ({ field }: TerminalRegistrationCodeInputProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- need to discard key
    const { name, key, ...fieldProps } = field;

    return (
        <Form.Item
            className="terminal-settings-form-item-control-registration-code"
            label="Terminal Registration Code"
            name={[name, "registrationCode"]}
            dependencies={[name, "label"]}
            rules={[
                {
                    message: "Invalid Terminal Registration Code",
                    pattern: /^[\w-]{1,}$/, // A-Z, 0-9, and Dashes only
                    whitespace: true,
                    required: true,
                },
            ]}
            validateTrigger={["onChange", "onBlur"]}
            {...fieldProps}
        >
            <Input />
        </Form.Item>
    );
};
